import { useEffect, useState } from 'react';

import { useTickets } from 'contexts/TicketsContext';
import { GetTicketsReportScheduleResponseDTO } from 'declarations/dto/reports';
import { ScheduleFilterValuesE, TicketsRefinementKeysE } from 'declarations/enums/tickets';
import { useFetchScheduleReports } from 'hooks/api/useReports';

type UseScheduleReportT = {
  scheduleReport: GetTicketsReportScheduleResponseDTO | null;
  isLoadingScheduleReport: boolean;
  totalTicketAmount: number;
  totalTicketCount: number;
};

const ageRangeMapping = {
  [ScheduleFilterValuesE.LessThan31Days]: { from: 0, to: 30 },
  [ScheduleFilterValuesE.From31To60Days]: { from: 31, to: 60 },
  [ScheduleFilterValuesE.From61To90Days]: { from: 61, to: 90 },
  [ScheduleFilterValuesE.From91To120Days]: { from: 91, to: 120 },
  [ScheduleFilterValuesE.Over120Days]: { from: 121, to: null },
};

const useScheduleReport = (): UseScheduleReportT => {
  /**
   * Hooks
   */
  const { getSearchParam } = useTickets();

  /**
   * Api hooks
   */
  const scheduleReportResult = useFetchScheduleReports({
    roofTopId: getSearchParam(TicketsRefinementKeysE.RoofTopId) || undefined,
    ageRanges: Object.values(ageRangeMapping),
  });

  /**
   * Varibles
   */
  const [totalTicketAmount, setTotalTicketAmount] = useState<number>(0);
  const [totalTicketCount, setTotalTicketCount] = useState<number>(0);

  /**
   * Effects
   */
  useEffect(() => {
    setTotalTicketAmount(
      scheduleReportResult.data?.data.reduce(
        (acc: any, data: { amount: any }) => acc + data.amount,
        0
      )
    );
    setTotalTicketCount(
      scheduleReportResult.data?.data.reduce(
        (acc: any, data: { count: any }) => acc + data.count,
        0
      )
    );
  }, [scheduleReportResult, setTotalTicketAmount, setTotalTicketCount]);

  return {
    scheduleReport: scheduleReportResult.data ? scheduleReportResult.data : null,
    isLoadingScheduleReport: scheduleReportResult.isLoading || scheduleReportResult.isRefetching,
    totalTicketCount,
    totalTicketAmount,
  };
};

export default useScheduleReport;
